import { AppConfig } from '@/constants/AppConfig';
import { AdminRetailerConstants, errorMessages } from '@/constants/constant';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { DetailedApiError } from '@/utilities/api/DetailedApiError';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { UserEndpoint } from '@api/endpoints';
import { RetailerEndpoint } from '@api/endpoints/RetailerEndpoint';
import { useMutation, useQuery, useQueryClient } from 'react-query';

const useAddRetailer =
  (setIsAddRetailerOpen: (args: boolean) => void, setError: (args: string) => void) => {
    const { openSnackbar: showSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const { mutate: addRetailer, isLoading } = useMutation(
      (args: RetailerEndpoint.Create.Request) =>
        RetailerApi.create(args),
      {
        onSuccess: async () => {
          showSnackbar(AdminRetailerConstants.addRetailerSuccess);
          setIsAddRetailerOpen(false);
          await queryClient.invalidateQueries(QueryKeys.GET_RETAILERS);
        },
        onError: (error: DetailedApiError) => {
          setError(error.message);
        },
      },
    );

    return { addRetailer, isLoading };
  };

const useEditRetailer =
(id = '', onClose: (args: boolean) => void, setError?: (args: string) => void) => {
  const { openSnackbar: showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { mutate: editRetailer, isLoading } = useMutation(
    async (args: RetailerEndpoint.Update.Request) => {
      if (id) {
        return await RetailerApi.update(id, args);
      }
      throw new Error('Retailer ID is required');
    },
    {
      onSuccess: async () => {
        onClose(false);
        showSnackbar(AdminRetailerConstants.updateRetailerSuccess);
        await queryClient.invalidateQueries(QueryKeys.GET_RETAILERS);
        await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_BY_ID);
      },
      onError: (error: DetailedApiError) => {
        setError ? setError(error.message) : showSnackbar(error.message);
      },
    },
  );

  return { editRetailer, isLoading };
};

const useGetRetailers = (
  query: RetailerEndpoint.List.Query,
  usePost = false,
  enabled = true,
  keepPreviousData = false,
) => {
  if (query.isActive && typeof query.isActive === 'string')
    delete query.isActive;
  const { data: getRetailers, isFetched, isFetching } = useQuery(
    [QueryKeys.GET_RETAILERS, query],
    () => RetailerApi.list(query, usePost),
    {
      enabled,
      keepPreviousData,
      staleTime: 5 * 60 * 1000,
    },
  );

  return { getRetailers, isFetched, isFetching };
};

const useGetRetailerById = (id: string) => {
  const { data: retailer, isLoading } = useQuery(
    [QueryKeys.GET_RETAILER_BY_ID, id],
    () => RetailerApi.get(id),
    {
      enabled: !!id,
    },
  );

  return { retailer, isLoading };
};

const useAddRetailerMember = (onClose: () => void, setErorr: (args: string) => void) => {
  const { openSnackbar: showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { mutate: addRetailerMember, isLoading } = useMutation(
    (args: UserEndpoint.Create.Request) =>
      RetailerApi.addRetailerMember(args),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_MEMBERS);
        await queryClient.invalidateQueries(QueryKeys.GET_LOCATION_MEMBERS);
        showSnackbar(AdminRetailerConstants.addMemberSuccess);
        onClose();
      },
      onError: (error: DetailedApiError) => {
        setErorr(error.message);
      },
    },
  );

  return { addRetailerMember, isLoading };
};

const useGetRetailerMembers = (id: string, search: string) => {
  const { data: retailerMembers, isLoading } = useQuery(
    [QueryKeys.GET_RETAILER_MEMBERS, id, search],
    () => RetailerApi.getRetailerMembers(id, search),
    {
      enabled: !!id,
    },
  );

  return { retailerMembers, isLoading };
};

const useUpdateMember =
  (id: string, isLocation = false, onClose: () => void, setError?: (args: string) => void) => {
    const { openSnackbar: showSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const { mutate: updateMember, isLoading } = useMutation(
      (args: UserEndpoint.Update.Request) =>
        RetailerApi.updateMember(id, args, isLocation),
      {
        onSuccess: async () => {
          const queryKey = isLocation ?
            QueryKeys.GET_LOCATION_MEMBERS : QueryKeys.GET_RETAILER_MEMBERS;

          await queryClient.invalidateQueries(queryKey);
          showSnackbar(AdminRetailerConstants.updateMemberSuccess);
          onClose();
        },
        onError: (error: DetailedApiError) => {
          setError ? setError(error.message) : showSnackbar(error.message);
        },
      },
    );

    return { updateMember, isLoading };
  };

const useDeleteMember = (id: string, onClose: () => void, setError: (args: string) => void) => {
  const { openSnackbar: showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { mutate: deleteMember, isLoading } = useMutation(
    () => RetailerApi.deleteMember(id),
    {
      onSuccess: async () => {
        showSnackbar(AdminRetailerConstants.deleteMemberSuccess);
        await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_MEMBERS);
        await queryClient.invalidateQueries(QueryKeys.GET_LOCATION_MEMBERS);
      },
      onError: (error: DetailedApiError) => {
        onClose();
        if(error.code === '500') {
          return setError(errorMessages.somethingWentWrong);
        }
        setError(error.message);
      },
    },
  );

  return { deleteMember, isLoading };
};

const useRetailerMetadata = () => {
  const isLoyaltyProgramDomain = window.location.hostname.includes(AppConfig.shortUrlDomain);
  const subdomain = window.location.hostname.split('.')[0];
  const { data: metadata } = useQuery(
    [QueryKeys.GET_LOYALTY_PROGRAM_METADATA, subdomain],
    () => RetailerApi.getLoyaltyProgramMetadata(subdomain),
    { enabled: isLoyaltyProgramDomain, staleTime: 5 * 60 * 1000 },
  );

  return { metadata };
};

export {
  useAddRetailer,
  useAddRetailerMember,
  useDeleteMember,
  useEditRetailer,
  useGetRetailerById,
  useGetRetailerMembers,
  useGetRetailers,
  useRetailerMetadata,
  useUpdateMember,
};
